import type {
  CreateOutletRequest,
  Outlet,
  OutletForPublic,
  UpdateOutletRequest,
} from '@billx/types';

import { publicApiClient, userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';
export class OutletService {
  /**
   * Create a new outlet
   */
  static async createOutlet(
    outletData: CreateOutletRequest
  ): Promise<NucleusServiceResponse<Outlet>> {
    try {
      const response = await userApiClient.post<NucleusServiceResponse<Outlet>>(
        '/outlets',
        outletData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create outlet');
    }
  }

  /**
   * Fetch outlets with optional filters
   */
  static async fetchOutletsByBusinessId(
    businessId: string
  ): Promise<NucleusServiceResponse<Outlet[]>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);

      const response = await userApiClient.get('/outlets', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch outlets');
    }
  }

  /**
   * Fetch outlets
   */
  static async fetchOutletsByBusinessIdForPublic(
    businessId: string
  ): Promise<NucleusServiceResponse<OutletForPublic[]>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);

      const response = await publicApiClient.get('/public/outlets', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch outlets');
    }
  }

  /**
   * Fetch a single outlet by ID
   */
  static async fetchOutletById(
    outletId: string
  ): Promise<NucleusServiceResponse<Outlet>> {
    try {
      const response = await userApiClient.get(`/outlets/${outletId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch outlet');
    }
  }

  /**
   * Fetch a single outlet by ID for public
   */
  static async fetchOutletByIdForPublic(
    outletId: string
  ): Promise<NucleusServiceResponse<OutletForPublic>> {
    try {
      const response = await publicApiClient.get(`/public/outlets/${outletId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch outlet');
    }
  }

  /**
   * Update an outlet
   */
  static async updateOutlet(
    outletId: string,
    updateData: UpdateOutletRequest
  ): Promise<NucleusServiceResponse<Outlet>> {
    try {
      const response = await userApiClient.patch<
        NucleusServiceResponse<Outlet>
      >(`/outlets/${outletId}`, updateData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update outlet');
    }
  }

  /**
   * Delete an (soft delete)
   */
  static async deleteOutlet(
    outletId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.delete(`/outlets/${outletId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete outlet');
    }
  }
}
